import type { WorkflowStep } from '@/api/workflows';

import { evaluateConditions } from './evaluateConditions';
import type { FieldValue } from './FieldRenderer';

/**
 * Fields borrowed by a RadioGroup / CheckboxGroup option (`revealFieldId`)
 * that is NOT currently revealed — because the option isn't selected, or its
 * owning group is itself hidden.
 *
 * These render nowhere, so they must not count toward required-completeness,
 * and must not appear on the Application Review either.
 * They can't be skipped by the ordinary field-condition check the way a hidden
 * section's fields are: a reveal target deliberately carries no conditions of
 * its own (the section walker bypasses them for borrowed fields), because its
 * visibility belongs to the owning option. Without this, ticking "No
 * contribution or transfer at this time" still blocked Next on the hidden
 * "Account number" the "Internal transfer" card would have revealed.
 */
export function hiddenBorrowedFieldIds(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
): Set<string> {
  const hidden = new Set<string>();
  if (step.type !== 'DynamicForm') return hidden;
  for (const section of step.sections) {
    const sectionVisible =
      (section.conditions?.length ?? 0) === 0 ||
      evaluateConditions(section.conditions, section.conditionCombinator, values);
    for (const row of section.rows) {
      for (const col of row.columns) {
        const owner = col.field;
        if (!owner || (owner.type !== 'CheckboxGroup' && owner.type !== 'RadioGroup')) continue;
        const ownerVisible =
          sectionVisible &&
          ((owner.conditions?.length ?? 0) === 0 ||
            evaluateConditions(owner.conditions, owner.conditionCombinator, values));
        // Bare field id: reveals inside a REPEATABLE section aren't supported
        // (the borrowed-field lookup is by bare id too), so there is no
        // instance-qualified key to read here. If one is ever authored there
        // this resolves to "not selected", which fails OPEN — the reveal
        // simply isn't treated as required — rather than blocking Next on a
        // field the advisor can't see.
        const raw = values[owner.id];
        // A CheckboxGroup holds string[]; a RadioGroup holds a scalar. The
        // null/empty guard matters on the radio side: an unanswered group is
        // undefined, which must not match an option whose value is ''.
        const isSelected = (optionValue: string): boolean =>
          owner.type === 'RadioGroup'
            ? raw !== undefined && raw !== null && raw !== '' && String(raw) === optionValue
            : ((raw as string[] | undefined) ?? []).includes(optionValue);
        for (const option of owner.options ?? []) {
          if (!option.revealFieldId) continue;
          if (!ownerVisible || !isSelected(option.value)) {
            hidden.add(option.revealFieldId);
          }
        }
      }
    }
  }
  return hidden;
}

// Exported for tests — the reveal/required interaction below is easy to
// regress and hard to reach through a full FormShell render.
